import React from "react";
import { Link } from "react-router-dom";
import { standaloneLeaderboardPath } from "../../../utils/leaguePath";
import {
  assetLabel,
  endedOnLabel,
  formatPrize,
  gameLengthLabel,
  metaLine,
  returnDisplay,
} from "./leagueFormat";
import { ChevronRightIcon } from "./icons";

// A finished Cash Game, on the Completed tab of the Cash Games View All screen.
//
// Client: "Here is how the 'Completed' Cash Games tab cards will look. Cards for
// mobile and desktop, not divider lines." So the Completed tab alone leaves the
// divided row list the other two tabs use — this supersedes the earlier "we are
// using our exact card system for these screens" for this tab only, and Starting
// soon and Live now are untouched.
//
// TWO CARDS, and which one is drawn is decided by the payout fields the
// organiser filled in, not by a flag:
//
//   "One attached only to a first-place winner. In Game creation, the prize pool
//    field and the 1st place fields will match. 2nd and 3rd places will be
//    empty."
//   "Card two is attached to the 1st place, 2nd, and 3rd place input fields.
//    (not prize pool)"
//
// So: no 2nd and no 3rd -> WINNER TAKES ALL, one figure, centred. Otherwise the
// podium -> WINNING RETURN beside PRIZES, all three amounts. formatPrize already
// returns null for blank, zero and negative, so "is there a payout here" is the
// same test podiumPayouts uses on the landing page rather than a second one.
//
// COLOURS ARE OURS, NOT STITCH'S. The client: "The stitch design visual cta is
// accurate. keep our colors consistent with what we use throughout league play.
// Stitch can drift. The highlighted money amounts you will notice are navy
// throughout cash games." So the arrangement is the design's and every value
// below is the token League Play already uses:
//
//   money            #0F172A — the navy the hero and the Live now card print
//                    every prize figure in
//   winning return   returnDisplay — #35B23C up, red down, grey flat
//   micro labels     text-tertiary, the compact card's eyebrow
//   title / meta     CashGameRow's, because this card replaces that row
//   CTA              "the cta's follow same thing we do in my leagues completed
//                    tab and cash games live" — #004C50, font-semibold, the same
//                    w-4 chevron as View Leaderboard on the Live now card
//
// Stitch's own 9px bold grey-400 metadata and its gray-50 rules are the drift
// the client is warning about, so they are not carried over.

// One micro label — "WINNING RETURN", "PRIZES", "WINNER TAKES ALL".
const MicroLabel = ({ children, className = "" }) => (
  <p
    className={`text-[10px] font-bold uppercase tracking-widest text-tertiary ${className}`}
  >
    {children}
  </p>
);

const CashGameCompletedCard = ({ game }) => {
  const ret = returnDisplay(game?.leading_performance);

  // First place is what a winner actually took. The client's rule is that on a
  // single-winner game it equals the prize pool, so the pool stands in only if
  // the field itself is empty — an older game published before the payout
  // fields existed still shows a figure rather than nothing.
  const first =
    formatPrize(game?.first_place_amount) || formatPrize(game?.cash_prize_amount);
  const second = formatPrize(game?.second_place_amount);
  const third = formatPrize(game?.third_place_amount);
  const winnerTakesAll = !second && !third;

  const meta = metaLine(assetLabel(game), gameLengthLabel(game), endedOnLabel(game));

  return (
    <Link
      /* Client, "Leaderboard Routing": "'View Results' CTA from any completed
         card must open the final leaderboard directly - not take people into the
         league home or the league shell." ANY completed card, so the cash game's
         result card goes to the same place My Leagues' does. */
      to={standaloneLeaderboardPath(game?.vanity_url)}
      className="group block rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors hover:border-gray-300 md:rounded-2xl md:p-5"
    >
      <h3 className="font-montserrat font-bold text-base md:text-lg leading-snug text-[#0F172A]">
        {game?.title}
      </h3>
      {meta && <p className="mt-0.5 text-xs text-[#6B7280]">{meta}</p>}

      {/* The result block. A game nobody qualified in has no return to print, so
          the block carries the payout alone rather than a 0.0% nobody earned —
          the same rule the Live now card follows for its leaderboard panel. */}
      {(ret || first) && (
        <div className="mt-3 border-y border-divider py-2.5">
          {winnerTakesAll ? (
            <div className="text-center">
              <MicroLabel>Winner takes all</MicroLabel>
              <div className="mt-0.5 flex flex-wrap items-baseline justify-center gap-x-2">
                {ret && (
                  <span className={`text-xl md:text-2xl font-bold ${ret.cls}`}>
                    {ret.text}
                  </span>
                )}
                {ret && first && (
                  <span className="text-xs md:text-sm font-medium text-secondary">
                    took
                  </span>
                )}
                {first && (
                  <span className="text-xl md:text-2xl font-bold text-[#0F172A]">
                    {first}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <MicroLabel>Winning return</MicroLabel>
                <p className={`mt-0.5 text-xl md:text-2xl font-bold ${ret ? ret.cls : "text-secondary"}`}>
                  {ret ? ret.text : "—"}
                </p>
              </div>
              <div className="flex flex-col justify-center">
                <MicroLabel>Prizes</MicroLabel>
                {/* First place leads at full weight in navy; the runners-up sit
                    a step down, which is the design's own hierarchy. */}
                <div className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5">
                  <span className="text-base md:text-lg font-bold text-[#0F172A]">
                    {first}
                  </span>
                  {[second, third].filter(Boolean).map((amount) => (
                    <React.Fragment key={amount}>
                      <span className="text-[10px] text-tertiary">•</span>
                      <span className="text-xs md:text-sm font-medium text-secondary">
                        {amount}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <span className="inline-flex items-center gap-1 text-sm md:text-base font-semibold text-[#004C50]">
          View Results
          <ChevronRightIcon className="h-4 w-4 md:h-5 md:w-5" />
        </span>
      </div>
    </Link>
  );
};

export default CashGameCompletedCard;
